import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {TreeNode} from "primeng/api";
import {OverlayPanel} from "primeng/overlaypanel";

import {classToClass, plainToClass} from 'class-transformer';

import {LocalCacheService, UserService} from '@app/core';

import {RoundUpDecimalPlace} from "@app/shared/classes/common";
import {AbstractItem, ItemCatalog} from "@app/shared/classes/item";
import {ChargeCodeType, ProjectChargeCode, PurchaseChargeCode} from "@app/shared/classes/charge-code";
import {PurchaseRequisitionItem} from "@app/shared/classes/purchase-req";

import {Format} from "@app/shared/enums/format.enum";
import {ItemType} from "@app/shared/enums/item-type.enum";
import {ItemTypeGroup} from '@app/shared/enums/item-type-group.enum';
import {Transaction} from "@app/shared/enums/txn-type.enum";
import {UserType} from '@app/shared/enums/user-type.enum';
import {Validation} from "@app/shared/enums/validation.enum";

import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {ItemCatalogService} from "@app/shared/services/item-catalog.service";
import {isWithinInterval, parse} from "date-fns";
import * as roundTo from "round-to";
import {CustomerInvoiceItem, PrimeNgConfig, PrimeNgSupportService, ProjectTask, TableColumn, VendorInvoiceItem} from "@app/shared";


/**
 * @author Jin Chun
 * @description common component to add line item for Purchase Requisition, Invoice, Receipt, Return, and Material Order.
 *  Show attributes based on transaction type (txnType)
 *                      InCat   PR   SO VenInv CusInv  Rec   MO BOM
 *  Quantity (Order)      N     Y    Y    R     R       N     Y   Y
 *  Quantity Received     N     N    N    Y     N       Y     N   N
 *  Quantity Delivered    N     N    N    N     Y       N     N   N
 *  Quantity Back Ordered N     N    N    R     R       R     R   N
 *  Unit Cost             Y     Y    N    Y     N       Y     Y   Y
 *  Sales Price           Y     N    Y    N     Y       N     N   N
 *  Actual Unit Cost      N     N    N    Y     N       N     N   N
 *  Actual Sales Price    N     N    Y    N     Y       N     N   N
 *  Extended Price        N     R    R    R     R       N     N   R
 *  Taxable               Y     Y    Y    Y     Y       N     N   N
 *  Line Notes            N     Y    Y    N     N       N     N   N   - for PO processed by PR
 *  Internal Notes        N     Y    Y    Y     Y       Y     N   N
 *  Item Notes            Y     R    R    R     R       R     R   R   - abstract item notes
 *  Charge Code           N     Y    N    Y     N       N     N   N
 *
 *
 *  Y - show, N - don't show, R - ready only if exists
 */

@Component({
  selector: 'app-add-line-item',
  templateUrl: './add-line-item.component.html',
  styleUrls: ['./add-line-item.component.css'],
  providers: [ItemCatalogService]
})

export class AddLineItemComponent implements OnInit, OnChanges {

  @Input() showDialog: boolean;
  @Input() salesTaxRate: number;
  @Input() viewOnly: boolean;
  @Input() txnType: Transaction;
  @Input() authorizedFrom: string;                                            // authorized charge code from date in local time zone
  @Input() authorizedThru: string;                                            // authorized charge code to date in local time zone
  @Input() itemType: ItemType;
  @Input() itemTypeGroup: ItemTypeGroup;
  @Input() projectId: number;
  @Input() requireProjectTask: boolean;
  @Input() requiredDeliveryDate: string;        // in dataservice date format
  @Input() filterChargeCodesBySelectedItemType: boolean = false;

  @Input() userId: number;  // to get charge codes based on submitted for

  // Array of PurchaseRequisition or CustomerInvoice items. Check and do not add any duplicate item
  @Input() existingItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | CustomerInvoiceItem>;
  @Input() existingSpecialItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | CustomerInvoiceItem>;

  @Input() itemToEdit: PurchaseRequisitionItem;

  @Output() onCloseDialog = new EventEmitter<boolean>();
  @Output() handler = new EventEmitter<PurchaseRequisitionItem>();

  Format: typeof Format = Format;
  ItemType: typeof ItemType = ItemType;
  ItemTypeGroup: typeof ItemTypeGroup = ItemTypeGroup;
  PrimeNgConfig = PrimeNgConfig;
  UserType: typeof UserType = UserType;
  Transaction: typeof Transaction = Transaction;
  Validation: typeof Validation = Validation;

  prItem: PurchaseRequisitionItem;

  catalogTree: TreeNode[];
  selectedCatalog: ItemCatalog;
  showProjectTaskTree: boolean = false;

  filteredAbstractItems: AbstractItem[];
  selectedAbstractItem: AbstractItem;
  requiredDeliveryDateWithinEffectiveDates: boolean;  // if service item, check if today

  tableColumns: TableColumn[];                                // column headers and fields to display charge code
  selectedChargeCode: PurchaseChargeCode;
  chargeCodeToEdit: PurchaseChargeCode;
  chargeCodesToExclude: PurchaseChargeCode[];           // existing charge codes - set when adding new item
  savedChargeCodesWithId: PurchaseChargeCode[];       // saved charge codes used to avoid backend unique db index issue
  includeChargeCodeTypes: ChargeCodeType[] = [];

  displayChargeCodeDialog: boolean;
  totalAllocation: number;
  availableAllocation: number;

  duplicateItemError: string;

  constructor(private cache: LocalCacheService, public itemCatalogService: ItemCatalogService, private transform: CommonTransformationService, public user: UserService,
              private primeNgService: PrimeNgSupportService) {
  }


  ngOnInit() {
    this.initTableColumns();
    this.getCatalogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.totalAllocation = 0;
    this.selectedAbstractItem = new AbstractItem();
    this.savedChargeCodesWithId = new Array<PurchaseChargeCode>();

    if (this.itemToEdit) {
      this.prItem = classToClass(this.itemToEdit);
      if (!this.prItem.purchaseChargeCodes) {
        this.prItem.purchaseChargeCodes = new Array<PurchaseChargeCode>();
      }
      this.selectedAbstractItem.id = this.prItem.abstractItemId;
      this.selectedAbstractItem.nameOrNumber = this.prItem.nameOrNumber;

      this.setRequiredDeliveryDateWithinEffectiveDates(this.prItem, this.requiredDeliveryDate, this.selectedAbstractItem);

      if (this.prItem.purchaseChargeCodes && this.prItem.purchaseChargeCodes.length > 0) {
        for (const chargeCode of this.prItem.purchaseChargeCodes) {
          this.totalAllocation += chargeCode.allocationPercent;
          if (chargeCode.id) {
            this.savedChargeCodesWithId.push(classToClass(chargeCode));
          }
        }
        this.totalAllocation *= 1;
      }
    } else {
      this.prItem = new PurchaseRequisitionItem();
    }
  }

  /**
   * @desc inform the parent component that the dialog has been closed
   */
  hideDialog(): void {
    this.onCloseDialog.emit(false);
    this.showDialog = false;
  }

  /**
   * @desc set the selected catalog and close the catalog panel
   */
  onNodeSelectCatalogTree(catalogNode: TreeNode<ItemCatalog>, op: OverlayPanel): void {
    this.selectedCatalog = catalogNode.data;
    op.hide();
  }

  /**
   * @desc search and filter items based on user's selected catalog, input item name, itemType, itemTypeGroup, and txnType
   */
  searchItem(nameToSearch: string, itemType: ItemType, itemTypeGroup: ItemTypeGroup, txnType: Transaction): void {

    this.itemCatalogService.searchFilteredItems(this.selectedCatalog.id, nameToSearch).subscribe(res => {
      this.filteredAbstractItems = plainToClass(AbstractItem, res.list);

      if (itemType) {
        if (itemType == ItemType.BillOfMaterials) {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => item.subtype == ItemType.Inventory || item.subtype == ItemType.BillOfMaterials);
        } else {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => item.subtype == itemType);
        }
      } else if (itemTypeGroup) {
        if (itemTypeGroup == ItemTypeGroup.Labor) {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => item.subtype == ItemType.Service || item.subtype == ItemType.Discount);
        } else if (itemTypeGroup == ItemTypeGroup.Expense) {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => item.subtype != ItemType.Service);
        }
      }

      if (txnType) {
        if (txnType === Transaction.MaterialOrder) {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => item.subtype == ItemType.Inventory || item.subtype == ItemType.BillOfMaterials || item.subtype == ItemType.Discount);
        } else if (txnType === Transaction.PurchaseRequisition || txnType === Transaction.VendorInvoice) {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => (item.subtype != ItemType.Discount && item.subtype != ItemType.Project));
        } else if (txnType === Transaction.SalesOrder || txnType === Transaction.CustomerInvoice) {
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => (item.subtype != ItemType.FixedAsset));
          this.filteredAbstractItems = this.filteredAbstractItems.filter(item => (ChargeCodeType.isDirect(item.codeType)));
        }
      }
    });
  }

  setRequiredDeliveryDateWithinEffectiveDates(prItem: PurchaseRequisitionItem, requiredDeliveryDate: string, selectedAbstractItem: AbstractItem): void {
    if (prItem.subtype == ItemType.Service && requiredDeliveryDate && selectedAbstractItem.effectiveFrom && selectedAbstractItem.effectiveThru) {
      this.requiredDeliveryDateWithinEffectiveDates = isWithinInterval(parse(this.requiredDeliveryDate, Format.DataserviceDate, new Date()),
        {start: parse(selectedAbstractItem.effectiveFrom, Format.DataserviceDate, new Date()), end: parse(selectedAbstractItem.effectiveThru, Format.DataserviceDate, new Date())});
    } else {
      this.requiredDeliveryDateWithinEffectiveDates = true;
    }
  }

  /**
   * @desc create new purchase req item using the input abstract item
   */
  onSelectItem(selectedAbstractItem: AbstractItem, txnType: Transaction): void {
    if (!this.prItem) {
      this.prItem = new PurchaseRequisitionItem();
    }

    this.prItem.abstractItemId = selectedAbstractItem.id;
    this.prItem.nameOrNumber = selectedAbstractItem.nameOrNumber;
    this.prItem.description = selectedAbstractItem.purchaseTxnDescription;
    this.prItem.subtype = selectedAbstractItem.subtype;
    this.prItem.fundingType = selectedAbstractItem.fundingType;
    this.prItem.isTaxable = selectedAbstractItem.isTaxable;
    this.prItem.unitCost = selectedAbstractItem.unitCost;
    this.prItem.unitOfMeasureId = selectedAbstractItem.unitOfMeasureId;
    this.prItem.unitOfMeasureDescription = selectedAbstractItem.unitOfMeasureDescription;
    this.prItem.commodityCodeId = selectedAbstractItem.commodityCodeId;
    this.prItem.commodityCodeDescription = selectedAbstractItem.commodityCodeDescription;

    this.prItem.vendorLaborCategoryId = selectedAbstractItem.vendorLaborCategoryId;
    this.prItem.projectName = selectedAbstractItem.projectName;
    this.prItem.projectLaborCategoryPositionTitle = selectedAbstractItem.projectLaborCategoryPositionTitle;

    // for a service item, if effective dates are available, check to see if the item can be added
    this.setRequiredDeliveryDateWithinEffectiveDates(this.prItem, this.requiredDeliveryDate, this.selectedAbstractItem);
    this.setFilterPropertiesForChargeCode(selectedAbstractItem.codeType);

    // set default quantity for special items
    if (this.prItem.subtype == ItemType.Shipping || this.prItem.subtype == ItemType.Discount) {
      if (txnType == Transaction.PurchaseRequisition || txnType == Transaction.SalesOrder || txnType == Transaction.MaterialOrder ||
        txnType == Transaction.BillOfMaterial) {
        this.prItem.qtyOrdered = 1;
      } else if (txnType == Transaction.VendorInvoice) {
        this.prItem.qtyReceived = 1;
      } else if (txnType == Transaction.CustomerInvoice) {
        this.prItem.qtyDelivered = 1;
      }
    }

    // Material Order specific fields
    if (txnType == Transaction.MaterialOrder) {
      this.prItem.manufacturerNumber = selectedAbstractItem.manufacturerNumber;
      this.prItem.quantityOnHand = selectedAbstractItem.quantityOnHand;
    } else if (txnType == Transaction.SalesOrder || txnType == Transaction.CustomerInvoice) {
      this.prItem.salesPrice = selectedAbstractItem.salesPrice;
    }

    this.checkDuplicateItem(this.prItem);
  }

  setFilterPropertiesForChargeCode(chargeCodeType: ChargeCodeType): void {
    if (this.filterChargeCodesBySelectedItemType) {
      this.includeChargeCodeTypes = [chargeCodeType];
    } else {
      this.includeChargeCodeTypes = null;
    }
  }

  /**
   * @desc calculates extended price for shipping and discount items
   */
  calculateExtendedPriceForDefaultItems(prItem: PurchaseRequisitionItem, txnType: Transaction): void {
    if (prItem.subtype == ItemType.Shipping || prItem.subtype == ItemType.Discount) {
      prItem.extendedPrice = prItem.unitCost;

      if (txnType == Transaction.VendorInvoice) {
        prItem.actualUnitCost = prItem.unitCost;
      } else if (txnType == Transaction.SalesOrder || txnType == Transaction.CustomerInvoice) {
        prItem.salesPrice = prItem.unitCost;
      }
    }
  }

  onClearItem(): void {
  }

  /**
   * @desc show dialog to add charge code
   */
  onClickAddChargeCode(): void {

    this.selectedChargeCode = null;
    this.chargeCodeToEdit = new PurchaseChargeCode();
    this.chargeCodesToExclude = this.prItem.purchaseChargeCodes;
    this.availableAllocation = this.calculateAvailableAllocation();
    this.chargeCodeToEdit.allocationPercent = this.availableAllocation;

    this.displayChargeCodeDialog = true;
  }

  /**
   * @desc make a copy of the selected charge code to edit
   */
  onClickEditChargeCode(chargeCode: PurchaseChargeCode): void {

    this.selectedChargeCode = chargeCode;
    this.chargeCodeToEdit = classToClass(chargeCode);

    // don't allow changing the charge code to one of the existing ones
    if (this.prItem.purchaseChargeCodes) {
      this.chargeCodesToExclude = classToClass(this.prItem.purchaseChargeCodes).filter(cc => cc.chargeCodeId != chargeCode.chargeCodeId);
    }
    this.availableAllocation = this.calculateAvailableAllocation(this.chargeCodeToEdit.allocationPercent);

    this.displayChargeCodeDialog = true;
  }

  /**
   * @desc set chargeCodeToEdit
   */
  handleSelectedChargeCode(chargeCode: ProjectChargeCode): void {
    this.chargeCodeToEdit.chargeCodeId = chargeCode.id;
    this.chargeCodeToEdit.chargeNumber = chargeCode.chargeNumber;
    this.chargeCodeToEdit.chargeCodeOwnerName = chargeCode.ownerName;
  }

  /**
   * @desc save the charge code to the table
   */
  onClickSaveChargeCode(chargeCode: PurchaseChargeCode): void {

    if (!this.selectedChargeCode) {
      // if the new charge code (chargeCodeToSave) was previously saved, need to reset / restore the id, in order to prevent unique index error from the backend db
      const savedChargeCodeWithId = this.savedChargeCodesWithId.find(cc => cc.chargeCodeId == chargeCode.chargeCodeId);
      if (savedChargeCodeWithId) {
        chargeCode.id = savedChargeCodeWithId.id;
      }
      this.prItem.purchaseChargeCodes = this.primeNgService.push(this.prItem.purchaseChargeCodes, chargeCode);
    } else {
      this.prItem.purchaseChargeCodes = this.primeNgService.replaceByObj(this.prItem.purchaseChargeCodes, chargeCode, this.selectedChargeCode);
    }
    this.totalAllocation = this.calculateTotalAllocation(this.prItem.purchaseChargeCodes);
    this.checkDuplicateItem(this.prItem);

    this.displayChargeCodeDialog = false;
  }

  /**
   * @desc delete the selected charge code from the table
   */
  onClickDeleteChargeCode(chargeCode: PurchaseChargeCode): void {

    const index = this.prItem.purchaseChargeCodes.indexOf(chargeCode);
    if (index >= 0) {
      this.prItem.purchaseChargeCodes = this.primeNgService.deleteById(this.prItem.purchaseChargeCodes, chargeCode.id);
      this.checkDuplicateItem(this.prItem);
      this.totalAllocation = this.calculateTotalAllocation(this.prItem.purchaseChargeCodes);
    }
    this.displayChargeCodeDialog = false;
  }

  /**
   * @desc calculate and set quantity back ordered
   */
  calculateQtyBackOrdered(selectedItem: PurchaseRequisitionItem, txnType: Transaction, salesTaxRate: number): void {

    if (selectedItem.qtyOrdered && selectedItem.qtyReceived) {
      selectedItem.qtyBackOrdered = roundTo(selectedItem.qtyOrdered - selectedItem.qtyReceived, RoundUpDecimalPlace);
    }
    this.itemCatalogService.updateTaxForPrItem(selectedItem, txnType, salesTaxRate);
  }


  hideChargeCodeDialog(): void {
    this.displayChargeCodeDialog = false;
  }

  /**
   * @desc set task id and name for the item; this is called with project task is selected
   */
  handleProjectTask(event: TreeNode<ProjectTask>): void {
    if (event && event.data) {
      this.prItem.taskId = event.data.id;
      this.prItem.taskName = event.data.name;
    }
  }

  /**
   * @desc show project task tree if event > 0
   */
  handleProjectTaskTreeSize(event: number): void {
    this.showProjectTaskTree = event > 0;
  }

  /**
   * @desc add new purchase item; make sure numbers are rounded and any rounding difference is fixed
   */
  onClickAddItem(item: PurchaseRequisitionItem, txnType: Transaction, salesTaxRate: number): void {

    this.itemCatalogService.updateExtendedPriceForPrItem(item, txnType);
    this.itemCatalogService.updateTaxForPrItem(item, txnType, salesTaxRate);
    this.itemCatalogService.updateChargeCodeAllocation(item);

    this.handler.emit(item);
    this.hideDialog();
  }

  private initTableColumns(): void {
    this.tableColumns = [
      {field: 'chargeNumber', header: 'Charge Code'},
      {field: 'allocationPercent', header: 'Allocation (%)'},
      {field: 'action', header: 'Action'},
    ];
  }

  /**
   * @desc get the catalog for display
   */
  private getCatalogs(): void {
    if (this.cache.itemCatalogs) {
      this.catalogTree = this.transform.convertToTree("name", this.cache.itemCatalogs);
    } else {
      this.itemCatalogService.getCatalogs().subscribe(catalogs => {
        this.cache.itemCatalogs = catalogs;
        this.catalogTree = this.transform.convertToTree("name", this.cache.itemCatalogs);
      });
    }
  }

  private calculateTotalAllocation(chargeCodes: PurchaseChargeCode[]): number {
    let total = 0;
    chargeCodes.forEach(cc => total += cc.allocationPercent);
    return total;
  }

  private calculateAvailableAllocation(currentAllocation?: number): number {
    return currentAllocation ? currentAllocation + 100 - this.totalAllocation : 100 - this.totalAllocation;
  }

  /**
   * check whether item is duplicate against the existing regular and special items
   */
  private checkDuplicateItem(item: PurchaseRequisitionItem): boolean {
    this.duplicateItemError = null;
    const isItemUnique = this.itemCatalogService.isItemUnique(item, this.existingItems, this.existingSpecialItems);

    if (!isItemUnique) {
      if (item.subtype == ItemType.Service) {
        this.duplicateItemError = `Cannot add duplicate service item with same charge code!`;
      } else {
        this.duplicateItemError = `Cannot add duplicate non-service item!`;
      }
    }
    return isItemUnique;
  }
}
